// Central registry for the TinyPDFs tools suite.
//
// Each tool has:
//   slug         — used for subdomain (<slug>.tinypdfs.com) and path (/tools/<slug>)
//   name         — display name
//   short        — one-line description for cards
//   icon         — emoji shown on cards (matches the spec from the product owner)
//   group        — group key (see TOOL_GROUPS)
//   status       — 'coming_soon' | 'beta' | 'live'
//
// To add a tool later: append to TOOLS; dashboard + routing pick it up automatically.

export const TOOL_GROUPS = [
  {
    key: 'management',
    title: 'PDF Management',
    description: 'Organize, rearrange, and restructure your PDFs.',
  },
  {
    key: 'edit',
    title: 'Editing & Enhancement',
    description: 'Polish and annotate your PDFs.',
  },
  {
    key: 'optimize',
    title: 'Optimization',
    description: 'Shrink PDFs without losing quality.',
  },
  {
    key: 'to_pdf',
    title: 'Convert to PDF',
    description: 'Turn images, text, and web pages into PDFs.',
  },
  {
    key: 'from_pdf',
    title: 'Convert from PDF',
    description: 'Extract images, text, or HTML from PDFs.',
  },
];

export const TOOLS = [
  // Management
  { slug: 'pdfmerger', name: 'PDF Merger', short: 'Combine multiple PDFs into one file.', icon: '📑', group: 'management', status: 'live' },
  { slug: 'pdfsplitter', name: 'PDF Splitter', short: 'Extract specific pages from a PDF.', icon: '✂️', group: 'management', status: 'live' },
  { slug: 'pdfrotator', name: 'PDF Page Rotator', short: 'Rotate individual pages in a PDF.', icon: '🔄', group: 'management', status: 'live' },

  // Edit
  { slug: 'pdfwatermark', name: 'PDF Watermark', short: 'Add text or image watermarks to PDFs.', icon: '💧', group: 'edit', status: 'live' },

  // Optimize
  { slug: 'pdfcompress', name: 'Compress PDF', short: 'Reduce PDF file size.', icon: '📦', group: 'optimize', status: 'live' },

  // To PDF
  { slug: 'imagetopdf', name: 'Image to PDF', short: 'Convert any image into a PDF.', icon: '🖼️', group: 'to_pdf', status: 'live' },
  { slug: 'jpgtopdf', name: 'JPG to PDF', short: 'Convert JPG images to PDF.', icon: '🖼️', group: 'to_pdf', status: 'live' },
  { slug: 'pngtopdf', name: 'PNG to PDF', short: 'Convert PNG images to PDF.', icon: '🖼️', group: 'to_pdf', status: 'live' },
  { slug: 'bmptopdf', name: 'BMP to PDF', short: 'Convert BMP images to PDF.', icon: '🖼️', group: 'to_pdf', status: 'live' },
  { slug: 'webptopdf', name: 'WebP to PDF', short: 'Convert WebP images to PDF.', icon: '🖼️', group: 'to_pdf', status: 'live' },
  { slug: 'txttopdf', name: 'TXT to PDF', short: 'Convert plain text files to PDF.', icon: '📄', group: 'to_pdf', status: 'live' },
  { slug: 'htmltopdf', name: 'HTML to PDF', short: 'Convert HTML pages to PDF.', icon: '🌐', group: 'to_pdf', status: 'live' },
  { slug: 'wordtopdf', name: 'Word to PDF', short: 'Convert .docx documents to PDF.', icon: '📝', group: 'to_pdf', status: 'live' },
  { slug: 'exceltopdf', name: 'Excel to PDF', short: 'Convert .xlsx / .csv spreadsheets to PDF.', icon: '📊', group: 'to_pdf', status: 'live' },

  // From PDF
  { slug: 'pdftojpg', name: 'PDF to JPG', short: 'Convert PDF pages to JPEG images.', icon: '🖼️', group: 'from_pdf', status: 'live' },
  { slug: 'pdftopng', name: 'PDF to PNG', short: 'Convert PDF pages to PNG images.', icon: '🖼️', group: 'from_pdf', status: 'live' },
  { slug: 'pdftowebp', name: 'PDF to WebP', short: 'Convert PDF pages to WebP images.', icon: '🖼️', group: 'from_pdf', status: 'live' },
  { slug: 'pdftotxt', name: 'PDF to TXT', short: 'Extract plain text from PDFs.', icon: '📄', group: 'from_pdf', status: 'live' },
  { slug: 'pdftohtml', name: 'PDF to HTML', short: 'Convert PDF documents to HTML.', icon: '🌐', group: 'from_pdf', status: 'live' },
  { slug: 'pdftoword', name: 'PDF to Word', short: 'Convert PDF files to editable Word documents.', icon: '📝', group: 'from_pdf', status: 'live' },
];

// Curated shortcut row on the dashboard.
export const POPULAR_SLUGS = ['pdfmerger', 'pdfcompress', 'jpgtopdf', 'pdftojpg'];

// Three top-level hero entry points.
export const HERO_ENTRY_POINTS = [
  {
    key: 'to_pdf',
    title: 'Convert to PDF',
    description: 'Turn images, text, and web pages into polished PDFs.',
    icon: '🖼️',
    groupKey: 'to_pdf',
  },
  {
    key: 'from_pdf',
    title: 'Convert from PDF',
    description: 'Extract images, text, or HTML from any PDF.',
    icon: '🔁',
    groupKey: 'from_pdf',
  },
  {
    key: 'edit_secure',
    title: 'Edit & Organize PDF',
    description: 'Merge, split, rotate, watermark, and compress.',
    icon: '🛠️',
    groupKeys: ['management', 'edit', 'optimize'],
  },
];

// Lookup helpers
const SLUG_SET = new Set(TOOLS.map((t) => t.slug));

export function isKnownToolSlug(slug) {
  if (!slug) return false;
  return SLUG_SET.has(String(slug).toLowerCase());
}

export function getToolBySlug(slug) {
  if (!slug) return null;
  const s = String(slug).toLowerCase();
  return TOOLS.find((t) => t.slug === s) || null;
}

export function getToolsByGroup(groupKey) {
  return TOOLS.filter((t) => t.group === groupKey);
}

export function getPopularTools() {
  return POPULAR_SLUGS.map(getToolBySlug).filter(Boolean);
}
